/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

const loadingIndicatorContainerStyle = {
  float: 'left',
  zIndex: 1,
  overflow: 'hidden',
} as React.CSSProperties;

const colorBackground = '#444444ff';
const colorLoading = '#287DFF';
const colorLoaded = '#00bf0e';

const loadingIndicatorStyle = {
  display: 'block',
  marginTop: '20px',
  marginLeft: '0x',
  padding: 0,
  position: 'absolute',
  background: colorBackground,
} as React.CSSProperties;

interface LoadingIndicatorProgressBarProps {
  progressPercentage: number;
  width?: number;
  height?: number;
  top?: number;
  left?: number;
}

const clamp = (number: number, min: number, max: number): number => {
  return Math.max(min, Math.min(number, max));
};

const LoadingIndicatorProgressBar = ({
  progressPercentage,
  width: loadingBarWidthInPx = 24,
  height: loadingBarHeightPx = 3,
  top = 20,
  left = 0,
}: LoadingIndicatorProgressBarProps): React.ReactElement => {
  const width = clamp(progressPercentage, 0, 100) / (100 / loadingBarWidthInPx);
  const style = {
    ...loadingIndicatorStyle,
    width: `${loadingBarWidthInPx}px`,
    height: `${loadingBarHeightPx}px`,
    marginTop: `${top}px`,
    marginLeft: `${left}px`,
  };
  return progressPercentage >= 0 ? (
    <span
      data-testid="loadDurationProgressBar"
      style={loadingIndicatorContainerStyle}
    >
      <span style={style} />
      <span
        data-testid="LoadingIndicatorProgressBarSpan"
        style={{
          ...style,
          width: `${width}px`,
          background: progressPercentage < 100 ? colorLoading : colorLoaded,
        }}
      />
    </span>
  ) : (
    <span />
  );
};

export default LoadingIndicatorProgressBar;
